/**
 * Coding-dropdown status filtering (SEE: hide inactive codes from pickers).
 *
 * ERP codes (projects/jobs, GL accounts, cost codes, vendors, employees, …) are
 * ingested with a normalized `status`. We keep every code in the database — so
 * historical transactions coded to a since-retired code still resolve — but we
 * hide retired codes from the coding pickers so cardholders only choose from
 * codes that are still in use.
 *
 * Rule: hide a code only when its status is *explicitly* one of the retired
 * states below. Active codes — and, deliberately, codes with a missing/blank or
 * unrecognized status — stay visible. Existing data has mixed and partial
 * statuses (lowercase from the AppXchange pipeline, capitalized from the legacy
 * Agave pipeline, and many with none at all), so "show only active" would wrongly
 * hide valid codes. "Hide only explicit retired states" is the safe inverse.
 *
 * For projects/jobs this also drops `complete` jobs: a finished job shouldn't be
 * a coding target for new spend any more than an inactive one.
 */

// Compared case-insensitively, so this covers both the AppXchange pipeline's
// lowercase values and the legacy Agave pipeline's capitalized ones.
const RETIRED_STATUSES = new Set([
  'inactive',
  'complete',
  'archived',
  'terminated',
  'deceased',
  'voided',
  'closed',
]);

type CodeLike = { status?: string | null } | null | undefined;

/** True when a code's status is an explicit retired state and it should be hidden from pickers. */
export const isRetiredCode = (code: CodeLike): boolean => {
  const status = code?.status?.trim().toLowerCase();
  return status ? RETIRED_STATUSES.has(status) : false;
};

/**
 * Drop retired (inactive/complete/…) codes, keeping active and unknown-status codes.
 * Generic over the entity type; the cast reads only the optional `status` field that
 * every coding entity carries, without constraining callers to a status-bearing type.
 */
export const filterSelectableCodes = <T>(codes: T[]): T[] =>
  codes.filter((code) => !isRetiredCode(code as CodeLike));
